<template>
  <mds-layout-grid :class="$style['hypo-fee-schedule-summary-list']">
    <mds-row v-if="options.showBackButton" :class="$style['back-button-row']">
      <mds-col>
        <mds-button
          :disabled="modifyingFeeSchedule"
          variation="flat"
          icon="caret-circle-left"
          type="button"
          @click="handleBackBtnClicked(true)"
        >
          <strong>{{ backButtonText || translate('goback') }}</strong>
        </mds-button>
      </mds-col>
    </mds-row>
    <div :class="$style['fee-schedule-content']">
      <FeeScheduleListPanelHeader
        :title="translate('assetBasedFee')"
        :description="computedDescriptionText"
        :primary-button-config="primaryButtonConfig"
        @click="applySelectedSchedule"
      />
      <mds-row v-if="!settings.isActualWorkflow">
        <mds-col>
          <div :class="$style['create-buttons-row']">
            <mds-button-container>
              <mds-button
                v-if="showCreateScheduleButton"
                :disabled="disableCreateBtn"
                :class="$style['create-button']"
                variation="secondary"
                icon="plus"
                type="button"
                @click="handleCreateSchedule"
              >
                {{ translate('createSchedule') }}
              </mds-button>
              <mds-button
                v-if="showCreateMaxFeeButton"
                :disabled="disableCreateBtn"
                :class="$style['create-button']"
                variation="secondary"
                icon="plus"
                type="button"
                @click="handleCreateMaxFee"
              >
                {{ translate('createMaxFee') }}
              </mds-button>
            </mds-button-container>
            <div :class="isProgramBasedMode ? $style['sort-buttons-row'] : ''">
              <div>
                {{ translate('sortBy') }}
                <mds-button
                  id="sort-button"
                  variation="flat"
                  icon-right="caret-down"
                  type="button"
                  :disabled="modifyingFeeSchedule"
                  @click="showSortDropdown = !showSortDropdown"
                  @blue="showSortDropdown = false"
                >
                  {{ sortType === 'name' ? translate('name') : translate('lastModified') }}
                </mds-button>
                <mds-popover
                  v-model="showSortDropdown"
                  triggered-by="sort-button"
                  :position="[`bottom-${isProgramBasedMode ? 'center' : 'left'}`]"
                  width="200px"
                >
                  <mds-list-group>
                    <mds-list-group-item
                      :text="translate('name')"
                      @click.native="sortMenuItemClicked('name')"
                    ></mds-list-group-item>
                    <mds-list-group-item
                      :text="translate('lastModified')"
                      @click.native="sortMenuItemClicked('lastModified')"
                    ></mds-list-group-item>
                  </mds-list-group>
                </mds-popover>
              </div>
              <div v-if="isProgramBasedMode">
                {{ translate('show') }}
                <mds-button
                  id="show-button"
                  variation="flat"
                  icon-right="caret-down"
                  type="button"
                  :disabled="modifyingFeeSchedule"
                  @click="showProgramDropdown = !showProgramDropdown"
                  @blue="showProgramDropdown = false"
                >
                  {{
                    programType === 'current'
                      ? translate('currentProgram')
                      : translate('allPrograms')
                  }}
                </mds-button>
                <mds-popover
                  v-model="showProgramDropdown"
                  triggered-by="show-button"
                  :position="[`bottom-${isProgramBasedMode ? 'center' : 'left'}`]"
                  width="200px"
                >
                  <mds-list-group>
                    <mds-list-group-item
                      :text="translate('currentProgram')"
                      @click.native="showProgramMenuItemClicked('current')"
                    ></mds-list-group-item>
                    <mds-list-group-item
                      :text="translate('allPrograms')"
                      @click.native="showProgramMenuItemClicked('all')"
                    ></mds-list-group-item>
                  </mds-list-group>
                </mds-popover>
              </div>
            </div>
          </div>
        </mds-col>
      </mds-row>
      <template v-if="!loading">
        <mds-row v-for="schedule in feeScheduleList" :key="schedule.id">
          <mds-col>
            <template v-if="isItemViewable(schedule)">
              <FeeScheduleItem
                :mwc-id="getMwcIdForChild('fee-schedule-item')"
                :schedule="schedule"
                :show-dot-menu="options.showDotMenu"
                :show-radio="options.showRadio"
                :editing-schedule-id="editingScheduleId"
                :fee-schedule-types="feeScheduleTypes"
                :show-info-icons="settings.showInfoIcons"
                :is-program-based-mode="isProgramBasedMode"
                :program-options="programOptions"
                :currency-symbol="options.currencySymbol"
                @schedule-selected="handleActiveFeeScheduleChanged"
                @item-edit-clicked="editingScheduleId = $event"
                @item-cancel-clicked="handleItemCancelClicked"
                @item-save-clicked="handleItemSaveClicked"
                @delete-schedule="handleItemDelete"
              >
              </FeeScheduleItem>
            </template>
          </mds-col>
        </mds-row>
      </template>
      <mds-loader v-else></mds-loader>
    </div>
    <mds-notification-container v-if="showNotification">
      <mds-notification
        key="current-program-not-selected-error"
        variation="error"
        @mds-notification-dismissed="showNotification = false"
      >
        {{
          `${translate('currentProgramNotSelectedErrorMessage')} ${
            programOptions.find(option => option.value === currentProgramId).text
          }`
        }}
      </mds-notification>
    </mds-notification-container>
  </mds-layout-grid>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import { MdsLayoutGrid, MdsRow, MdsCol } from '@mds/layout-grid';
import { MdsButtonContainer, MdsButton } from '@mds/button';
import { MdsListGroup, MdsListGroupItem } from '@mds/list-group';
import { MdsNotification, MdsNotificationContainer } from '@mds/notification';
import MdsLoader from '@mds/loader';
import cloneDeep from 'lodash.clonedeep';
import MdsPopover from 'hypo-shared-utils/src/components/ScrollablePopover';
import defaultConfig from './_config';
import FeeScheduleListPanelHeader from './common/FeeScheduleListPanelHeader';
import FeeScheduleItem from './FeeScheduleItem';
import { getDefaultNewFeeScheduleData, registerEventHandler } from './utils';
import { feeScheduleTypeOptions, EditingAction } from './_config/constants';
import DataSourceMixin from './mixins/data-source-mixin';
import FeeSchedulesMixin from './mixins/fee-schedules-mixin';

export default {
  name: 'hypo-fee-schedule-summary-list',
  components: {
    MdsLayoutGrid,
    MdsRow,
    MdsCol,
    MdsButtonContainer,
    MdsButton,
    FeeScheduleListPanelHeader,
    FeeScheduleItem,
    MdsPopover,
    MdsListGroup,
    MdsListGroupItem,
    MdsLoader,
    MdsNotification,
    MdsNotificationContainer,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin, DataSourceMixin, FeeSchedulesMixin],
  props: {
    mwcId: {
      type: String,
      required: true,
    },
    config: {
      type: [String, Object],
      default: () => {},
    },
    environment: String,
    userId: String,
    currencySymbol: {
      type: String,
      default: '$',
    },
    showBackButton: {
      type: Boolean,
      default: true,
    },
    backButtonText: {
      type: String,
    },
    showApply: {
      type: Boolean,
      default: true,
    },
    showDotMenu: {
      type: Boolean,
      default: true,
    },
    showRadio: {
      type: Boolean,
      default: true,
    },
    descriptionText: {
      type: String,
    },
    showCreateButtons: {
      type: Object,
      default: () => ({ createSchedule: true, createMaxFee: true }),
    },
    showFeeTypes: {
      type: Object,
      default: () => ({
        showMaxFee: true,
        showFeeSchedule: true,
      }),
    },
    showFeeScheduleTypes: {
      type: Object,
      default: () => ({
        tieredFeeByIncrementalValue: true,
        tieredFeeByBreakpoint: true,
        totalFee: true,
        mixed: true,
      }),
    },
    viewableSources: {
      type: Object,
      default: () => {},
    },
    autoSelected: {
      type: Boolean,
      default: true,
    },
    activedFeeScheduleId: {
      type: String,
      default: '',
    },
    isProgramBasedMode: {
      type: Boolean,
      default: false,
    },
    currentProgramId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      defaultConfig: {
        intlNamespace: this.mwcId,
        ...defaultConfig,
      },
      feeScheduleList: [],
      editingScheduleId: null,
      editingAction: EditingAction.NONE,
      ready: false,
      showSortDropdown: false,
      sortType: 'name',
      showProgramDropdown: false,
      programType: 'current',
      loading: false,
      showNotification: false,
      programOptions: [],
    };
  },
  computed: {
    modifyingFeeSchedule() {
      return this.editingScheduleId !== null;
    },
    selectedScheduleId() {
      const schedule = this.feeScheduleList.find(item => item.isActived === true);
      return (schedule && schedule.id) || '';
    },
    isAnyFeeScheduleTypeAvailable() {
      return this.feeScheduleTypes.length > 0;
    },
    feeScheduleTypes() {
      return feeScheduleTypeOptions.filter(
        option =>
          this.options.showFeeScheduleTypes && this.options.showFeeScheduleTypes[option.labelKey]
      );
    },
    disableCreateBtn() {
      return !this.ready || this.modifyingFeeSchedule;
    },
    showCreateScheduleButton() {
      return (
        this.options.showCreateButtons &&
        this.options.showCreateButtons.createSchedule &&
        this.isAnyFeeScheduleTypeAvailable
      );
    },
    showCreateMaxFeeButton() {
      return this.options.showCreateButtons && this.options.showCreateButtons.createMaxFee;
    },
    computedDescriptionText() {
      const defaultText =
        !this.options.showApply || this.modifyingFeeSchedule
          ? this.translate('createDescription')
          : this.translate('applyDescription');
      return this.descriptionText || this.settings.isActualWorkflow
        ? this.translate('actualWorkflowDescription')
        : defaultText;
    },
    applyButtonDisabled() {
      if (this.settings.isActualWorkflow) {
        return false;
      }
      return !this.selectedScheduleId || this.modifyingFeeSchedule;
    },
    currentProgram() {
      return this.programOptions.find(option => option.value === this.currentProgramId);
    },
    isInvalidProgramMode() {
      return this.isProgramBasedMode && this.currentProgram !== undefined;
    },
    primaryButtonConfig() {
      const titleText = this.settings.isActualWorkflow
        ? this.translate('close')
        : this.translate('apply');
      return {
        text: titleText,
        visible: this.options.showApply,
        disabled: this.applyButtonDisabled,
      };
    },
  },
  watch: {
    'feeScheduleList.length': {
      handler(val) {
        if (
          (!this.settings.useExternalActiveStatus || this.settings.isActualWorkflow) &&
          val === 1 &&
          this.autoSelected
        ) {
          this.feeScheduleList[0].isActived = true;
        }
      },
    },
    'options.activedFeeScheduleId': function() {
      this.setActivedFeeSchedule();
    },
    'options.userId': function() {
      if (this.options.userId) {
        this.$emit('ready');
      }
    },
  },
  created() {
    if (typeof this.environment !== 'undefined') {
      this.options.environment = this.environment;
    }
    this.$on('ready', this.handleFeeSchedulesDataReady);
  },
  mounted() {
    const terminationEvent = 'onpagehide' in window ? 'pagehide' : 'unload';
    this.unregisterEvent = registerEventHandler(window, terminationEvent, event => {
      if (!event.persisted) {
        this.emitUpdatedFeeSchedulesData();
      }
    });
  },
  beforeDestroy() {
    this.emitUpdatedFeeSchedulesData();
    this.unregisterEvent();
  },
  methods: {
    async handleFeeSchedulesDataReady() {
      this.loading = true;
      try {
        await this.getAndProcessProgramOptions();
        await this.updateFeeSchedulesData();
        this.setActivedFeeSchedule();
        this.emitUpdatedFeeSchedulesData();
        this.ready = true;
      } finally {
        this.loading = false;
      }
    },
    async updateFeeSchedulesData() {
      const feeSchedules = await this.getFeeScheduleList();
      this.setFeeScheduleList(feeSchedules);
      this.sortFeeScheduleList();
      this.addAnnualFeeNoLessThanForPreviouslyCreatedMaxFee();
    },
    setActivedFeeSchedule() {
      if (!this.settings.useExternalActiveStatus) return;
      const activedFeeSchedule = this.feeScheduleList.find(
        schedule => schedule.feeScheduleId === this.options.activedFeeScheduleId
      );
      if (activedFeeSchedule) {
        this.updateActivedFeeScheduleById(activedFeeSchedule.id);
      } else {
        this.feeScheduleList.forEach(schedule => {
          schedule.isActived = false;
        });
      }
    },
    updateActivedFeeScheduleById(id) {
      this.feeScheduleList.forEach(schedule => {
        if (schedule.id === id) {
          schedule.isActived = true;
        } else {
          schedule.isActived = false;
        }
      });
    },
    setFeeScheduleList(feeSchedules) {
      this.feeScheduleList = feeSchedules.map((schedule, index) => {
        return {
          ...schedule,
          id: index + 1,
        };
      });
    },
    async applySelectedSchedule() {
      const selectedSchedule = this.feeScheduleList.find(i => i.id === this.selectedScheduleId);
      const invalidProgramMode =
        this.isProgramBasedMode &&
        (!selectedSchedule.programList ||
          !selectedSchedule.programList.includes(this.currentProgramId));

      if (invalidProgramMode) {
        this.showNotification = true;
        return;
      }
      if (!selectedSchedule || !selectedSchedule.feeScheduleId) {
        this.handleBackBtnClicked();
        return;
      }
      if (!this.settings.useExternalActiveStatus && !this.settings.isActualWorkflow) {
        await this.applyFeeSchedule(selectedSchedule.feeScheduleId);
      }

      this.$emit('active-schedule-changed', this.cleanDataBeforeEmit(selectedSchedule));
      this.emitUpdatedFeeSchedulesData();
    },
    cleanDataBeforeEmit(selectedSchedule) {
      const {
        feeScheduleId,
        feeScheduleType,
        feeScheduleDetails,
        name,
        allowMaxDiscount,
        maxDiscount,
        isMaxFee,
        programList,
        maxFee,
        annualFeeNoLessThan,
        separateAccountManagerFee,
      } = selectedSchedule;
      let result = {
        id: feeScheduleId,
        name,
        allowMaxDiscount,
        maxDiscount,
        feeScheduleDetails,
      };
      if (this.isProgramBasedMode) {
        result = {
          ...result,
          programList,
        };
      }
      if (isMaxFee) {
        result = {
          ...result,
          isMaxFee,
          maxFee,
          annualFeeNoLessThan,
          separateAccountManagerFee,
        };
        if (this.settings.isActualWorkflow) {
          result = { ...result, programName: selectedSchedule.programName };
        }
      } else {
        result = {
          ...selectedSchedule,
          ...result,
          feeScheduleType,
        };
      }
      return result;
    },
    handleCreateSchedule() {
      this.createNewFeeSchedule();
    },
    handleCreateMaxFee() {
      this.createNewFeeSchedule(true);
    },
    createNewFeeSchedule(isMaxFee) {
      const maxId = this.feeScheduleList.reduce(
        (acc, schedule) => (acc > schedule.id ? acc : schedule.id),
        0
      );

      let defaultFeeData = {
        id: maxId + 1,
        allowMaxDiscount: this.settings.isMaxDiscountAllowed,
        maxDiscount: this.settings.isMaxDiscountAllowed ? this.settings.defaultMaxDiscount : null,
      };
      if (isMaxFee) {
        defaultFeeData = {
          ...defaultFeeData,
          isMaxFee: true,
          annualFeeNoLessThan: this.settings.isMaxDiscountAllowed
            ? this.settings.defaultAnnualFeeNoLessThan
            : null,
        };
      } else {
        const defaultFeeScheduleType =
          (this.feeScheduleTypes[0] && this.feeScheduleTypes[0].value) || 0;
        defaultFeeData = {
          ...defaultFeeData,
          feeScheduleType: defaultFeeScheduleType,
        };
      }
      const newSchedule = getDefaultNewFeeScheduleData(defaultFeeData);
      this.feeScheduleList.unshift(newSchedule);
      this.editingScheduleId = maxId + 1;
      this.editingAction = EditingAction.CREATE;
    },
    handleItemCancelClicked() {
      if (this.editingAction === EditingAction.CREATE) {
        this.feeScheduleList = this.feeScheduleList.filter(
          item => item.id !== this.editingScheduleId
        );
      }
      this.completeEditing();
    },
    async handleItemSaveClicked(schedule) {
      const index = this.feeScheduleList.findIndex(item => item.id === schedule.id);
      const { id, ...newScheduleData } = schedule;
      this.feeScheduleList.splice(index, 1, cloneDeep(schedule));
      this.completeEditing();
      this.loading = true;
      try {
        await this.createOrUpdateFeeSchedule(newScheduleData);
        await this.updateFeeSchedulesData();
      } finally {
        this.loading = false;
      }
    },
    completeEditing() {
      this.editingScheduleId = null;
      this.editingAction = EditingAction.NONE;
    },
    handleItemDelete(id) {
      const index = this.feeScheduleList.findIndex(schedule => schedule.id === id);
      if (index === -1) {
        return;
      }
      const deleteFeeItem = this.feeScheduleList[index];
      const { feeScheduleId } = deleteFeeItem ?? {};
      this.feeScheduleList = [
        ...this.feeScheduleList.slice(0, index),
        ...this.feeScheduleList.slice(index + 1),
      ];
      if (this.activedFeeScheduleId === feeScheduleId) {
        this.$emit('active-schedule-changed', null);
      }
      if (this.settings.isActualWorkflow) {
        return;
      }
      this.deleteFeeSchedule(feeScheduleId);
    },
    handleActiveFeeScheduleChanged(id) {
      this.updateActivedFeeScheduleById(id);
    },
    isItemViewable(schedule) {
      const control = this.viewableSources;
      const hideFirmSaved = schedule.isFirmSaved && control && !control.firmSaved;
      const hideNormalUserSaved = !schedule.isFirmSaved && control && !control.normalUserSaved;
      const hideFirmData = schedule.isFirmData && control && !control.firmData;
      const hideMaxFeeData = schedule.isMaxFee && !this.showFeeTypes.showMaxFee;
      const hideFeeScheduleData = !schedule.isMaxFee && !this.showFeeTypes.showFeeSchedule;
      let viewable = true;
      if (this.isProgramBasedMode) {
        viewable = this.isProgramBasedFeeScheduleViewable(schedule);
      } else if (this.settings.isActualWorkflow && this.options.activedFeeScheduleId) {
        viewable = schedule.isActived;
      }
      return (
        !(
          hideFirmSaved ||
          hideNormalUserSaved ||
          hideFirmData ||
          hideMaxFeeData ||
          hideFeeScheduleData
        ) && viewable
      );
    },
    sortFeeScheduleList() {
      if (this.sortType === 'name') {
        this.feeScheduleList.sort((itemX, itemY) => itemX.name.localeCompare(itemY.name));
      } else {
        this.feeScheduleList.sort((itemX, itemY) => {
          const timeX = new Date(itemX.lastModifiedDate).getTime();
          const timeY = new Date(itemY.lastModifiedDate).getTime();
          if (timeX > timeY) {
            return -1;
          }
          if (timeX < timeY) {
            return 1;
          }
          return 0;
        });
      }
    },
    sortMenuItemClicked(sortType) {
      this.showSortDropdown = false;
      this.sortType = sortType;
      this.sortFeeScheduleList();
    },
    isProgramBasedFeeScheduleViewable(schedule) {
      if (this.programType === 'all') {
        return true;
      }
      const { programList = [] } = schedule;
      return this.currentProgram && programList.includes(this.currentProgram.value);
    },
    addAnnualFeeNoLessThanForPreviouslyCreatedMaxFee() {
      this.feeScheduleList.forEach(schedule => {
        if (schedule.isMaxFee && schedule.allowMaxDiscount) {
          const expectedValue = schedule.maxFee * (1 - schedule.maxDiscount / 100);
          const actualValue = schedule.annualFeeNoLessThan;
          if (!actualValue || Math.abs(actualValue - expectedValue) > 0.0001) {
            schedule.annualFeeNoLessThan = expectedValue;
          }
        }
      });
    },
    showProgramMenuItemClicked(programType) {
      this.showProgramDropdown = false;
      this.programType = programType;
    },
    getAndProcessProgramOptions() {
      if (!this.isProgramBasedMode) {
        return Promise.resolve();
      }
      return this.getProgramsInfo().then(programsInfo => {
        this.programOptions = programsInfo.item1.map(program => {
          return { text: program.name, value: program.programId };
        });
      });
    },
    handleBackBtnClicked(backButtonClicked) {
      this.emitUpdatedFeeSchedulesData();
      this.$emit('back-button-clicked', backButtonClicked);
    },
  },
};
</script>

<style lang="scss" module>
@import '~@mds/constants';
@import './styles/variables';
@import '@mds/typography';

.hypo-fee-schedule-summary-list {
  @include mds-body-text-m();
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-bottom: $mds-space-2-and-a-half-x;

  .back-button-row {
    width: 100%;
  }

  .fee-schedule-content {
    width: 70%;
    max-width: 800px;
    .main-header {
      @include mds-body-text-l();
    }
    .apply-button {
      display: flex;
      justify-content: flex-end;
      margin-top: $mds-space-1-x;
    }
    .create-buttons-row {
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
      align-items: center;
      margin-top: $mds-space-2-x;
      .sort-buttons-row {
        width: 100%;
        margin-top: $mds-space-2-x;
        display: flex;
        .mds-button {
          margin-right: $mds-space-2-x;
        }
      }
    }
  }

  .mds-notification .mds-notification__dismiss-button {
    position: absolute;
    right: 16px;
    top: 16px;
  }
}
</style>
